import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "./ui/dropdown-menu";
import { ChevronDown, UserCog, User, ShieldCheck, Pin, Check } from "lucide-react";
import { toast } from "sonner";

const LABELS = {
  platform_owner: "Platform Owner",
  organisation_owner: "Organisation Owner",
  campaign_manager: "Campaign Manager",
  national_manager: "National Manager",
  regional_manager: "Regional Manager",
  team_leader: "Team Leader",
  fundraiser: "Fundraiser",
  trustee: "Trustee",
};

const isManagement = (ctx) =>
  ["platform_owner", "organisation_owner", "campaign_manager",
    "national_manager", "regional_manager", "team_leader"].includes(ctx);

/**
 * OCI V2.5B — Context Switcher.
 * Shows the user's active context with a dropdown of their
 * availableContexts. Switching:
 *   - posts to /api/context/switch
 *   - navigates to /fundraiser when switching INTO fundraiser
 *   - navigates to /owner when switching INTO a management context
 * The dropdown is silent (no toast) if the user only has 1 context —
 * just a passive label.
 */
export function ContextSwitcher({ compact = false }) {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await apiClient.get("/context/current");
      setState(r.data);
    } catch (e) {
      // Silent — context endpoint failure shouldn't break the page
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !state) return null;
  const { activeContext, availableContexts = [], defaultContext } = state;
  if (availableContexts.length <= 1) {
    // Single-context user — render a passive label only (no menu)
    return compact ? null : (
      <div className="text-xs text-slate-500 flex items-center gap-1"
           data-testid="context-switcher-passive">
        <User className="h-3 w-3" />
        {LABELS[activeContext] || activeContext}
      </div>
    );
  }

  const handleSwitch = async (next) => {
    if (next === activeContext) return;
    setSwitching(true);
    try {
      await apiClient.post("/context/switch", { context: next });
      toast.success(`Switched to ${LABELS[next] || next}`);
      // Navigate to the surface that matches the new context
      if (next === "fundraiser") navigate("/dashboard", { replace: true });
      else if (isManagement(next)) navigate("/owner", { replace: true });
      else navigate("/", { replace: true });
      // Re-fetch state (the new page will re-render with the new context)
      window.location.reload();
    } catch (e) {
      toast.error("Switch failed: " + (e?.response?.data?.detail || e.message));
      setSwitching(false);
    }
  };

  const handleSetDefault = async (ctx) => {
    try {
      await apiClient.put("/context/default", { defaultContext: ctx });
      toast.success(`Default context set to ${LABELS[ctx] || ctx}`);
      reload();
    } catch (e) {
      toast.error("Could not set default: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm"
                disabled={switching}
                data-testid="context-switcher-trigger"
                className="gap-2">
          {isManagement(activeContext)
            ? <UserCog className="h-3.5 w-3.5" />
            : activeContext === "trustee"
              ? <ShieldCheck className="h-3.5 w-3.5" />
              : <User className="h-3.5 w-3.5" />}
          <span className="text-xs font-medium">
            {LABELS[activeContext] || activeContext}
          </span>
          <Badge variant="outline" className="text-[10px] px-1 py-0">
            {availableContexts.length}
          </Badge>
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Switch context</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={activeContext} onValueChange={handleSwitch}>
          {availableContexts.map((ctx) => (
            <DropdownMenuRadioItem key={ctx} value={ctx}
              data-testid={`context-option-${ctx}`}>
              <div className="flex items-center justify-between flex-1 gap-2">
                <span className="flex items-center gap-2">
                  {isManagement(ctx) ? <UserCog className="h-3.5 w-3.5 text-blue-600" />
                                       : ctx === "trustee" ? <ShieldCheck className="h-3.5 w-3.5 text-violet-600" />
                                                              : <User className="h-3.5 w-3.5 text-emerald-600" />}
                  <span>{LABELS[ctx] || ctx}</span>
                </span>
                <span className="flex items-center gap-1">
                  {ctx === defaultContext && (
                    <Pin className="h-3 w-3 text-amber-500" aria-label="default" />
                  )}
                  {ctx === activeContext && (
                    <Check className="h-3 w-3 text-emerald-600" />
                  )}
                </span>
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
          Set default
        </DropdownMenuLabel>
        {availableContexts.map((ctx) => (
          <DropdownMenuItem key={`def-${ctx}`}
            onClick={() => handleSetDefault(ctx)}
            disabled={ctx === defaultContext}
            data-testid={`context-default-${ctx}`}
            className="text-xs">
            <Pin className="h-3 w-3 mr-2 text-slate-400" />
            Make {LABELS[ctx] || ctx} default
            {ctx === defaultContext && <Check className="h-3 w-3 ml-auto text-emerald-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
